import PropTypes from 'prop-types';

import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import FormGroup from '@zenbusiness/zui/FormGroup';
import FormHelperText from '@zenbusiness/zui/FormHelperText';
import FormLabel from '@zenbusiness/zui/FormLabel';

/**
 * Multi select field using checkboxes
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {Array} props.value - Array of selected values
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 */
function MultiSelectField({ def, value = [], onChange, disabled = false, error }) {
  const handleChange = (optionId, optionLabel) => (event) => {
    const isChecked = event.target.checked;
    const currentValues = Array.isArray(value) ? value : [];

    let newValues;
    if (isChecked) {
      // Add to selection if within limits
      if (!def.maxSelected || currentValues.length < def.maxSelected) {
        newValues = [...currentValues, { selectedId: optionId, value: optionLabel }];
      } else {
        newValues = currentValues; // Don't add if at limit
        return;
      }
    } else {
      // Remove from selection
      newValues = currentValues.filter((opt) => opt.selectedId !== optionId);
    }

    onChange(newValues);
  };

  const fieldId = `multi-select-${def.id}`;
  const hasError = Boolean(error);
  const currentValues = Array.isArray(value) ? value : [];
  const isAtLimit = def.maxSelected && currentValues.length >= def.maxSelected;

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
    >
      {def.label && (
        <FormLabel component="legend" id={`${fieldId}-label`} required={def.required}>
          {def.label}
          {def.maxSelected && (
            <span style={{ fontSize: '0.875rem', fontWeight: 'normal', marginLeft: '8px' }}>
              (Select up to {def.maxSelected})
            </span>
          )}
        </FormLabel>
      )}

      <FormGroup>
        {def.options?.map((option) => {
          const isChecked = currentValues.some((opt) => opt.selectedId === option.id);
          const isDisabled = disabled || (isAtLimit && !isChecked);

          return (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={isChecked}
                  color="primary"
                  disabled={isDisabled}
                  name={`${fieldId}-${option.id}`}
                  onChange={handleChange(option.id, option.label)}
                />
              }
              disabled={isDisabled}
              label={option.label}
            />
          );
        })}
      </FormGroup>

      {(error || def.helpText) && <FormHelperText>{error || def.helpText}</FormHelperText>}
    </FormControl>
  );
}

MultiSelectField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['multi_select']).isRequired,
    label: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    maxSelected: PropTypes.number,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default MultiSelectField;
